import { useTranslations } from '@vocab/react';
import {
  MenuRenderer,
  Box,
  MenuItem,
  Strong,
  Hidden,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import type * as React from 'react';

import { COMMON_SORT_OPTIONS } from 'src/constants/sortOrder';

import translations from './.vocab';
import { SortOrderDropdownTrigger } from './SortOrderDropdownTrigger';
import { getActiveSortOption, getSortOptions } from './getSortDropdownDetails';

interface Props {
  sortBy: string;
  hasSearchText: boolean;
  hasQuestion: boolean;
  onSortByChange: (params: { sortBy: string }) => Promise<void>;
  align: React.ComponentProps<typeof MenuRenderer>['align'];
  trigger?: React.ReactNode;
}

const SortOrderDropdown: React.FC<Props> = ({
  sortBy,
  hasSearchText,
  hasQuestion,
  onSortByChange,
  align,
  trigger,
}) => {
  const { t } = useTranslations(translations);

  const options = getSortOptions(COMMON_SORT_OPTIONS, hasQuestion);

  const activeLabel = getActiveSortOption(sortBy, options);

  if (hasSearchText) {
    return <></>;
  }

  return (
    <Box data-testid="sort-dropdown" className="sortDropdown">
      <MenuRenderer
        align={align}
        offsetSpace="small"
        trigger={(triggerProps, { open }) => (
          <Box
            {...triggerProps}
            cursor="pointer"
            width="full"
            data={{ intercomid: 'sortByDropdown' }}
          >
            {trigger ? (
              trigger
            ) : (
              <SortOrderDropdownTrigger
                label={
                  activeLabel ? (
                    <>
                      {t('Sorted by sortOption', {
                        HiddenBelowDesktop: (children) => (
                          <Hidden below="desktop">{children}</Hidden>
                        ),
                        Strong: (children) => <Strong>{children}</Strong>,
                        sortOption: t(activeLabel),
                      })}
                    </>
                  ) : (
                    <Hidden below="desktop">{t('Sorted by')}</Hidden>
                  )
                }
                direction={open ? 'up' : 'down'}
              />
            )}
          </Box>
        )}
      >
        {options.map(({ label: optionLabel, value: optionValue }) => (
          <MenuItem
            key={optionValue}
            onClick={() => onSortByChange({ sortBy: optionValue })}
          >
            {t(optionLabel)}
          </MenuItem>
        ))}
      </MenuRenderer>
    </Box>
  );
};

export default observer(SortOrderDropdown);
